import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ChevronLeft, ChevronRight, Play, X, Sparkles, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import { GlowOrb } from "@/components/common/GlowOrb";
import { trackViewOnce } from "@/lib/analytics";

const SITE_URL = "https://www.cloudlabweb.com";

function withAutoplay(url, autoplay = true) {
  if (!url) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("autoplay", autoplay ? "true" : "false");
    return u.toString();
  } catch {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}autoplay=${autoplay ? "true" : "false"}`;
  }
}

// ---- Reused mini components ----
function PhoneFrame({ children }) {
  return (
    <div className="relative mx-auto max-w-[280px]">
      <div aria-hidden className="absolute -inset-4 rounded-[48px] bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 blur-2xl opacity-70" />
      <div className="relative rounded-[42px] p-[7px] bg-gradient-to-b from-neutral-800 to-neutral-950 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08),_0_30px_70px_-30px_rgba(0,0,0,0.85)]">
        <div className="relative rounded-[35px] overflow-hidden bg-black" style={{ aspectRatio: "9 / 19.5" }}>
          {children}
          <div aria-hidden className="absolute top-1.5 left-1/2 -translate-x-1/2 w-20 h-4 rounded-full bg-black/90 z-10" />
          <div aria-hidden className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-16 h-1 rounded-full bg-white/40 z-10" />
        </div>
      </div>
    </div>
  );
}

function ImageLightbox({ src, alt, onPrev, onNext, onClose, index, total }) {
  useEffect(() => {
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid="portfolio-detail-image-lightbox"
    >
      <button onClick={onClose} className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20 z-20" aria-label="Close">
        <X className="w-4 h-4" />
      </button>
      {onPrev && (
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20 z-20" aria-label="Previous">
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {onNext && (
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20 z-20" aria-label="Next">
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
      <motion.img
        key={src}
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
        src={src}
        alt={alt}
        className="max-h-[85vh] max-w-[92vw] rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono text-white/60">
        {index + 1} / {total}
      </div>
    </motion.div>
  );
}

function VideoLightbox({ item, onClose }) {
  const isPortrait = item.media_format === "portrait";
  useEffect(() => {
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid="portfolio-detail-video-lightbox"
    >
      <button onClick={onClose} className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20 z-20" aria-label="Close">
        <X className="w-4 h-4" />
      </button>
      <div onClick={(e) => e.stopPropagation()} className={isPortrait ? "w-full max-w-[380px]" : "w-full max-w-5xl"}>
        {isPortrait ? (
          <PhoneFrame>
            <iframe title={item.title} src={withAutoplay(item.video_url, true)} referrerPolicy="strict-origin-when-cross-origin" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full border-0" />
          </PhoneFrame>
        ) : (
          <div className="rounded-3xl overflow-hidden border border-white/10 bg-black shadow-[0_60px_120px_-40px_rgba(0,0,0,0.6)]">
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe title={item.title} src={withAutoplay(item.video_url, true)} referrerPolicy="strict-origin-when-cross-origin" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full border-0" />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ---- Gallery scroller ----
function GalleryScroller({ images, onOpen }) {
  const trackRef = useRef(null);
  const scrollBy = (dir) => {
    const el = trackRef.current; if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };
  if (!images?.length) return null;
  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Gallery</div>
          <div className="mt-1 font-heading font-bold text-2xl">Selected shots</div>
        </div>
        <div className="hidden md:flex gap-2">
          <button onClick={() => scrollBy(-1)} className="w-10 h-10 rounded-full border border-border hover:border-primary hover:text-primary transition-colors grid place-items-center" aria-label="Scroll left">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => scrollBy(1)} className="w-10 h-10 rounded-full border border-border hover:border-primary hover:text-primary transition-colors grid place-items-center" aria-label="Scroll right">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div
        ref={trackRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar pb-4 -mx-2 px-2"
        data-testid="portfolio-detail-gallery"
      >
        {images.map((src, i) => (
          <button
            key={i}
            onClick={() => onOpen(i)}
            className="group relative shrink-0 snap-start w-[85%] md:w-[62%] lg:w-[46%] aspect-[16/10] rounded-2xl overflow-hidden border border-border/60 bg-foreground/5 hover:border-primary/50 transition-all"
            data-testid={`portfolio-detail-gallery-item-${i}`}
          >
            <img
              src={src}
              alt={`Shot ${i + 1}`}
              className="w-full h-full object-cover transform-gpu group-hover:scale-105 transition-transform duration-700"
              onError={(e) => { e.currentTarget.style.opacity = "0"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-3 right-3 text-[10px] font-mono bg-black/60 text-white px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              {i + 1} / {images.length}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- Detail page ----
export default function PortfolioDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [category, setCategory] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoOpen, setVideoOpen] = useState(false);
  const [imgIdx, setImgIdx] = useState(null); // null = closed

  useEffect(() => {
    setLoading(true);
    setItem(null);
    api.get(`/portfolio/${slug}`)
      .then(async ({ data }) => {
        setItem(data);
        // GA4/GTM: case_study_view (deduped per page-load)
        trackViewOnce("case_study_view", {
          slug: data.slug,
          title: data.title,
          category: data.category_slug,
        });
        // Fetch category name + related items in same category
        const [cats, siblings] = await Promise.all([
          api.get("/portfolio-categories"),
          api.get(`/portfolio?category=${data.category_slug}`),
        ]);
        setCategory((cats.data || []).find((c) => c.slug === data.category_slug) || null);
        setRelated((siblings.data || []).filter((s) => s.id !== data.id).slice(0, 3));
      })
      .catch(() => setItem(false))
      .finally(() => setLoading(false));
  }, [slug]);

  const galleryImages = useMemo(() => {
    if (!item) return [];
    return item.gallery?.length ? item.gallery : [item.cover_image].filter(Boolean);
  }, [item]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 max-w-7xl mx-auto px-6" data-testid="portfolio-detail-loading">
        <div className="h-6 w-32 bg-foreground/10 rounded animate-pulse" />
        <div className="mt-6 h-16 w-3/4 bg-foreground/10 rounded animate-pulse" />
        <div className="mt-10 aspect-video w-full bg-foreground/5 rounded-3xl animate-pulse" />
      </div>
    );
  }
  if (item === false) {
    return (
      <div className="min-h-screen pt-32 max-w-3xl mx-auto px-6 text-center" data-testid="portfolio-detail-not-found">
        <Seo title="Portfolio piece not found" noindex />
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Portfolio · 404</div>
        <h1 className="mt-4 font-heading font-black text-5xl">We couldn&apos;t find that piece.</h1>
        <p className="mt-4 text-muted-foreground">It may have been unpublished or moved. Head back to the portfolio to keep exploring.</p>
        <Link to="/portfolio" className="mt-8 inline-flex items-center gap-2 rounded-full px-5 py-3 bg-primary text-primary-foreground text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to portfolio
        </Link>
      </div>
    );
  }

  const hasVideo = !!item.video_url;
  const isPortrait = item.media_format === "portrait";

  return (
    <div className="relative min-h-screen pt-24 md:pt-32" data-testid="portfolio-detail-page">
      <Seo
        title={`${item.title} · Portfolio`}
        description={item.subtitle || item.description || `${item.title} — a CloudLab Technologies portfolio piece.`}
        keywords={`${item.title}, ${category?.name || ""}, CloudLab portfolio, ${(item.tech_stack || []).join(", ")}`}
        image={item.cover_image?.startsWith("/") ? `${SITE_URL}${item.cover_image}` : item.cover_image}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: item.title,
            description: item.subtitle || undefined,
            url: `${SITE_URL}/portfolio/${item.slug}`,
            image: item.cover_image?.startsWith("/") ? `${SITE_URL}${item.cover_image}` : item.cover_image,
            dateCreated: item.year || undefined,
            creator: { "@id": `${SITE_URL}/#organization` },
            ...(item.client ? { sourceOrganization: { "@type": "Organization", name: item.client } } : {}),
          },
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Portfolio", path: "/portfolio" },
            { name: item.title, path: `/portfolio/${item.slug}` },
          ]),
        ]}
      />
      <GlowOrb className="top-10 -left-20" color="primary" size={520} />
      <GlowOrb className="bottom-20 -right-20" color="secondary" size={520} />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 mb-6">
        <Link to="/portfolio" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors" data-testid="portfolio-detail-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to portfolio
        </Link>
      </div>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 pb-12">
        <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-10 items-center">
          {/* Copy */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-primary">
              <Sparkles className="w-3.5 h-3.5" />
              {category?.name || "Portfolio"} {item.year && <span className="text-muted-foreground">· {item.year}</span>}
            </div>
            <h1 className="mt-4 font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              {item.title}
            </h1>
            {item.subtitle && (
              <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">{item.subtitle}</p>
            )}

            {/* Meta chips */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {item.client && (
                <span className="text-[11px] font-mono uppercase tracking-widest text-foreground bg-foreground/5 border border-border px-2.5 py-1 rounded-full">
                  Client · {item.client}
                </span>
              )}
              {(item.tech_stack || []).map((t) => (
                <span key={t} className="text-[10px] font-mono px-2 py-1 rounded-full bg-primary/8 text-primary border border-primary/20">
                  {t}
                </span>
              ))}
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {hasVideo && (
                <button
                  onClick={() => setVideoOpen(true)}
                  className="rounded-full px-6 py-3 bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center gap-2 shadow-[0_15px_40px_-15px_hsl(var(--primary)/0.6)]"
                  data-testid="portfolio-detail-play"
                >
                  <Play className="w-4 h-4 fill-current" /> {isPortrait ? "Play reel" : "Play video"}
                </button>
              )}
              {item.external_url && (
                <a
                  href={item.external_url}
                  target={item.external_url.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="rounded-full px-6 py-3 border border-border text-sm inline-flex items-center gap-2 hover:bg-foreground/5"
                  data-testid="portfolio-detail-external"
                >
                  Visit live <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <Link to="/quote" className="rounded-full px-6 py-3 bg-foreground text-background text-sm font-semibold inline-flex items-center gap-2">
                Work with us <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* Visual */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1, duration: 0.7 }} className="relative">
            {isPortrait ? (
              <PhoneFrame>
                <img src={item.cover_image} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
                {hasVideo && (
                  <button onClick={() => setVideoOpen(true)} className="absolute inset-0 z-[6] grid place-items-center" aria-label="Play reel">
                    <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-[0_15px_35px_-10px_hsl(var(--primary)/0.9)]">
                      <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
                      <Play className="w-5 h-5 fill-current relative z-10 translate-x-0.5" />
                    </span>
                  </button>
                )}
              </PhoneFrame>
            ) : (
              <div className="relative rounded-3xl overflow-hidden border border-border shadow-[0_45px_100px_-40px_rgba(0,0,0,0.5)]">
                <div className="relative aspect-[16/10]">
                  <img src={item.cover_image} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
                  {hasVideo && (
                    <button onClick={() => setVideoOpen(true)} className="absolute inset-0 grid place-items-center bg-black/20 hover:bg-black/30 transition-colors" aria-label="Play video">
                      <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-[0_20px_50px_-15px_hsl(var(--primary)/0.9)]">
                        <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
                        <Play className="w-7 h-7 fill-current relative z-10 translate-x-0.5" />
                      </span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Outcomes strip */}
        {item.outcomes?.length > 0 && (
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {item.outcomes.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-border/70 bg-card/60 backdrop-blur p-5"
              >
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{o.metric}</div>
                <div className="mt-1 font-heading font-black text-3xl md:text-4xl text-primary">{o.value}</div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Description */}
      {item.description && (
        <section className="relative max-w-3xl mx-auto px-6 py-10">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">About this piece</div>
          <div className="mt-3 text-base md:text-lg leading-relaxed text-foreground/85 whitespace-pre-line">
            {item.description}
          </div>
        </section>
      )}

      {/* ---- Standardised case-study sections (render only when data exists) ---- */}
      {(item.challenge || item.solution) && (
        <section className="relative max-w-6xl mx-auto px-6 py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {item.challenge && (
              <div className="rounded-3xl border border-border bg-card p-7 md:p-8" data-testid="portfolio-detail-challenge">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">The Challenge</div>
                <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">What we had to solve.</h2>
                <p className="mt-4 text-base leading-relaxed text-foreground/85 whitespace-pre-line">{item.challenge}</p>
              </div>
            )}
            {item.solution && (
              <div className="rounded-3xl border border-border bg-card p-7 md:p-8" data-testid="portfolio-detail-solution">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">The Solution</div>
                <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">What we built.</h2>
                <p className="mt-4 text-base leading-relaxed text-foreground/85 whitespace-pre-line">{item.solution}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {item.features?.length > 0 && (
        <section className="relative max-w-6xl mx-auto px-6 py-8 md:py-12" data-testid="portfolio-detail-features">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Features shipped</div>
          <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">What&apos;s in the box.</h2>
          <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {item.features.map((feat, i) => (
              <li
                key={i}
                className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border"
                data-testid={`portfolio-detail-feature-${i}`}
              >
                <span className="mt-0.5 shrink-0 w-2 h-2 rounded-full bg-primary" />
                <span className="text-sm leading-relaxed">{feat}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(item.services?.length > 0 || item.industries?.length > 0) && (
        <section className="relative max-w-6xl mx-auto px-6 py-6" data-testid="portfolio-detail-related-links">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Related</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(item.services || []).map((s) => (
              <Link key={s} to={`/services/${s}`} data-testid={`portfolio-related-service-${s}`} className="px-3 py-1.5 rounded-full border border-border text-sm hover:border-primary/50 transition-colors">
                {s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </Link>
            ))}
            {(item.industries || []).map((s) => (
              <Link key={s} to={`/industries/${s}`} data-testid={`portfolio-related-industry-${s}`} className="px-3 py-1.5 rounded-full border border-border text-sm hover:border-primary/50 transition-colors">
                {s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} industry
              </Link>
            ))}
          </div>
        </section>
      )}

      {(item.tech_stack?.length > 0 || item.timeline) && (
        <section className="relative max-w-6xl mx-auto px-6 py-8 md:py-12" data-testid="portfolio-detail-tech-timeline">
          <div className="grid md:grid-cols-[1fr_auto] gap-8 items-start">
            {item.tech_stack?.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Tech Stack</div>
                <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">Built with.</h2>
                <div className="mt-5 flex flex-wrap gap-2.5">
                  {item.tech_stack.map((t) => (
                    <span
                      key={t}
                      className="px-3.5 py-1.5 rounded-full bg-background border border-border text-sm font-medium hover:border-primary/50 hover:text-primary transition-colors"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {item.timeline && (
              <div className="min-w-[220px] rounded-3xl border border-border bg-card p-6" data-testid="portfolio-detail-timeline">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Timeline</div>
                <div className="mt-2 font-heading font-black text-2xl md:text-3xl text-primary leading-tight">{item.timeline}</div>
              </div>
            )}
          </div>
        </section>
      )}

      {item.process?.length > 0 && (
        <section className="relative max-w-6xl mx-auto px-6 py-10 md:py-14" data-testid="portfolio-detail-process">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Delivery Process</div>
          <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">How we shipped it.</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {item.process.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`portfolio-detail-process-${i}`}
              >
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-heading font-black text-lg bg-primary text-primary-foreground">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-4 font-heading font-bold text-lg leading-tight">{p.step}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Gallery */}
      {galleryImages.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 py-10 md:py-14">
          <GalleryScroller images={galleryImages} onOpen={setImgIdx} />
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 py-10 md:py-14">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">More in {category?.name || "this category"}</div>
              <div className="mt-1 font-heading font-bold text-2xl md:text-3xl">Keep exploring</div>
            </div>
            <Link to="/portfolio" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-1">
              All work <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link key={r.id} to={`/portfolio/${r.slug}`} className="group block rounded-3xl overflow-hidden border border-border/60 bg-card/60 hover:border-primary/40 transition-all hover:-translate-y-0.5" data-testid={`portfolio-detail-related-${r.slug}`}>
                <div className="aspect-[4/3] overflow-hidden bg-foreground/5">
                  <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-primary">{category?.name || ""}</div>
                  <div className="mt-1 font-heading font-semibold line-clamp-2">{r.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Lightboxes */}
      <AnimatePresence>
        {videoOpen && <VideoLightbox item={item} onClose={() => setVideoOpen(false)} />}
        {imgIdx !== null && (
          <ImageLightbox
            src={galleryImages[imgIdx]}
            alt={`${item.title} · shot ${imgIdx + 1}`}
            index={imgIdx}
            total={galleryImages.length}
            onClose={() => setImgIdx(null)}
            onPrev={imgIdx > 0 ? () => setImgIdx(imgIdx - 1) : null}
            onNext={imgIdx < galleryImages.length - 1 ? () => setImgIdx(imgIdx + 1) : null}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
